import React, { useState, useRef, useEffect } from 'react';
import { Menu, ChevronDown, RotateCcw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { useLanguage } from '../../features/language/useLanguage';
import { NotificationBell } from '../common/NotificationBell';
import { useToast } from '../common/Toast';
import { SubscriptionTierBadge } from './SubscriptionTierBadge';
import { useOnboardingStore } from '../../store/onboardingStore';

interface HeaderProps {
  onMenuClick: () => void;
}

export const Header: React.FC<HeaderProps> = ({ onMenuClick }) => {
  const user = useAuthStore((state) => state.user);
  const navigate = useNavigate();
  const { languageCode, setLanguageCode, t } = useLanguage();
  const { showToast } = useToast();
  const [isLangDropdownOpen, setIsLangDropdownOpen] = useState(false);
  const onboardingStatus = useOnboardingStore((s) => s.state?.status);
  const replayTour = useOnboardingStore((s) => s.replay);
  const canReplay = onboardingStatus === 'completed' || onboardingStatus === 'skipped';
  const [isReplaying, setIsReplaying] = useState(false);
  const replayLabel = t('common.header.replayTour', undefined, 'Replay tour');
  const langDropdownRef = useRef<HTMLDivElement>(null);

  const handleReplay = async () => {
    if (isReplaying) return;
    setIsReplaying(true);
    try {
      await replayTour();
      // Success flips onboarding status to in_progress, so canReplay turns false and
      // this button unmounts — no need to reset isReplaying (avoids a spinner flash).
      navigate('/inventory');
    } catch {
      showToast(t('common.header.replayTourError', undefined, 'Could not restart the tour. Please try again.'), 'error');
      setIsReplaying(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (langDropdownRef.current && !langDropdownRef.current.contains(event.target as Node)) {
        setIsLangDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <header className="bg-white shadow-sm h-16 flex items-center justify-between px-6" data-test-id="header">
      <div className="flex items-center">
        <button
          type="button"
          data-test-id="header-menu-button"
          className="md:hidden -ml-2 mr-4 p-2 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
          onClick={onMenuClick}
        >
          <Menu className="h-6 w-6" aria-hidden="true" />
        </button>
        <h1 className="text-xl font-semibold text-gray-800">
          {/* Dynamic Title could go here */}
        </h1>
      </div>
      <div className="flex items-center">
        <NotificationBell />
        {canReplay && (
          <button
            type="button"
            data-test-id="header-replay-tour"
            onClick={handleReplay}
            disabled={isReplaying}
            aria-busy={isReplaying}
            aria-label={replayLabel}
            title={replayLabel}
            className="group mr-4 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1.5 text-sm font-medium text-gray-500 transition-colors hover:bg-primary-50 hover:text-primary-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/60 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-70"
          >
            <RotateCcw
              className={`h-4 w-4 transition-transform duration-500 ${isReplaying ? 'animate-spin' : 'group-hover:-rotate-180'}`}
              aria-hidden="true"
            />
            <span className="hidden sm:inline">{replayLabel}</span>
          </button>
        )}
        <div
          className="relative mr-4"
          ref={langDropdownRef}
          data-test-id="header-language-selector"
        >
          <button
            type="button"
            className="flex items-center space-x-1 text-sm font-semibold text-gray-700 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500 rounded px-2 py-1"
            onClick={() => setIsLangDropdownOpen(!isLangDropdownOpen)}
            aria-expanded={isLangDropdownOpen}
            aria-haspopup="true"
          >
            <span>{languageCode.toUpperCase()}</span>
            <ChevronDown className="h-4 w-4" />
          </button>

          {isLangDropdownOpen && (
            <div className="absolute right-0 mt-2 w-24 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
              <div className="py-1" role="menu" aria-orientation="vertical">
                <button
                  type="button"
                  className={`block w-full text-left px-4 py-2 text-sm transition-colors ${
                    languageCode === 'en'
                      ? 'font-bold text-primary-600 bg-gray-50'
                      : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                  }`}
                  role="menuitem"
                  data-test-id="header-language-option-en"
                  onClick={() => {
                    setLanguageCode('en');
                    setIsLangDropdownOpen(false);
                  }}
                >
                  English
                </button>
                <button
                  type="button"
                  className={`block w-full text-left px-4 py-2 text-sm transition-colors ${
                    languageCode === 'id'
                      ? 'font-bold text-primary-600 bg-gray-50'
                      : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                  }`}
                  role="menuitem"
                  data-test-id="header-language-option-id"
                  onClick={() => {
                    setLanguageCode('id');
                    setIsLangDropdownOpen(false);
                  }}
                >
                  Indonesia
                </button>
              </div>
            </div>
          )}
        </div>
        <button
          type="button"
          className="ml-4 flex items-center rounded-md focus:outline-none"
          data-test-id="header-user-profile"
          onClick={() => navigate('/settings')}
        >
          <span className="text-sm font-medium text-gray-700 mr-2 hover:underline">
            {user?.name || t('common.user.fallbackName')}
          </span>
          <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-500">
            {user?.name?.charAt(0) || t('common.user.fallbackName').charAt(0)}
          </div>
        </button>
        <SubscriptionTierBadge />
      </div>
    </header>
  );
};
